import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps each paid « Gravure personnalisée » line in step with the ring it belongs to (same
 * `_lien_gravure` property, see blocks/varmier-engraving.liquid):
 * - ring quantity changed → engraving quantity follows;
 * - ring removed, or engraving without its ring → engraving removed.
 *
 * Runs when the cart is shown and after every cart change, then announces its own fix with a
 * CartLinesUpdateEvent so the cart page, the drawer and the cart icon refresh as usual.
 */

let reconciling = false;

/**
 * @param {Array<{ key: string, quantity: number, properties?: Record<string, string> | null }>} items
 * @returns {Record<string, number>} Line key → quantity it must have
 */
function linkedUpdates(items) {
  /** @type {Map<string, number>} */
  const rings = new Map();
  for (const item of items) {
    const link = item.properties?._lien_gravure;
    if (link && !item.properties?.Emplacement) rings.set(link, (rings.get(link) ?? 0) + item.quantity);
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const item of items) {
    const link = item.properties?._lien_gravure;
    if (!link || !item.properties?.Emplacement) continue;
    const wanted = rings.get(link) ?? 0;
    if (item.quantity !== wanted) updates[item.key] = wanted;
  }
  return updates;
}

/** @param {Array<any>} items */
async function reconcile(items) {
  if (reconciling) return;
  const updates = linkedUpdates(items);
  if (Object.keys(updates).length === 0) return;

  reconciling = true;
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
  const kept = lines.filter((line) => line.quantity > 0);

  // Standard events: removals are announced as « remove », quantity changes as « update » (quantity > 0)
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: kept.length ? 'update' : 'remove',
      context: 'cart',
      lines: kept.length ? kept : lines,
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
    });
    const cart = await response.json();
    if (cart.status) throw new Error(cart.description || cart.message || 'Engraving sync failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections ?? undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'varmier-engraving-cart',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[varmier-engraving] Could not sync the engraving lines:', error);
  } finally {
    reconciling = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  promise
    ?.then((/** @type {any} */ result) => {
      const items = result?.detail?.items;
      if (Array.isArray(items)) reconcile(items);
    })
    .catch(() => {});
});

// A cart opened later (other tab, back button) may hold an orphan engraving line
fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
  .then((response) => response.json())
  .then((cart) => reconcile(cart.items ?? []))
  .catch(() => {});
